<template>
  <div
    v-if="text"
    id="textAdId"
    :class="['textad-container']"
    :style="`color:${fontColor};text-align:${textAlign};`"
  >
    <div
      class="textad-wrapper"
      :style="backgroundStyle ? backgroundStyle : ''"
    >
      <div
        v-if="isValidTextField(text?.attributes?.eyebrow)"
        :class="[text?.attributes?.eyebrowStyleType, 'eyebrow']"
        :style="`${text?.attributes?.eyebrowStyleType}`==='boxedEyebrow' ? { background: text?.attributes?.eyebrowBgColor ?? `#000` } : ''"
        v-html="sanitize(text?.attributes?.eyebrow, cleanStyles)"
      />
      <div
        v-if="text?.logo?.attributes?.isLogoPresent === 'true'"
        class="logo-block"
        :style="logoStyleAttributes"
      >
        <ImageMap
          v-if="text?.logo"
          :image-map="text?.logo"
        />
      </div>
      <div
        v-if="textAdContentTop.length"
        class="text-block"
        :class="{'chart-visible': isPromoChartVisible}"
        v-html="sanitize(textAdContentTop, cleanStyles)"
      />
      <div
        v-if="isPromoChartVisible"
        class="promo-chart margin-top-m"
        aria-label="Promo Chart"
      >
        <PromoChart
          :promo-chart="text?.table"
          :style="{ color: text?.attributes?.fontColor }"
        />
      </div>
      <div
        v-if="textAdContentBottom.length"
        class="text-block"
        :class="{'margin-top-m': isPromoChartVisible}"
        v-html="sanitize(textAdContentBottom, cleanStyles)"
      />
      <Ctas
        class="cta"
        :ctas="text?.ctas"
        :alignment="text?.attributes?.alignment"
      />
    </div>
  </div>
</template>

<script setup>
import {
  computed, ref,
} from 'vue';
import { sanitize } from '@vue-util/sanitize';
import ImageMap from '../imageMap';
import cleanStyles from '../../helpers/getCleanStyles';
import Ctas from '../ctas';
import PromoChart from './PromoChart';
import shouldShowChart from '../../helpers/showPromoChart';
import { isValidTextField } from '../../helpers/cleanHtml';

const props = defineProps({
  text: {
    type: Object,
    default: () => {},
  },
  topLevelComp: {
    type: Boolean,
    default: false,
  },
  applyGlobalStyles: {
    type: Boolean,
    default: true,
  },
  textArray: {
    type: Array,
    default: () => [
      'heading',
      'disclaimer',
      'caption',
      'subBody',
      'subHeading',
      'smallLink',
      'body',
      'subHeading2',
      'body2',
      'subHeading3',
      'body3',
    ],
  },
});

const isPromoChartVisible = computed(() => shouldShowChart(props.text));

const createTextContent = (textArray = []) => {
  const textAdAttributes = props.text?.attributes || {};
  return textArray
    .filter((key) => key in textAdAttributes && isValidTextField(textAdAttributes[key]))
    .map((key) => {
      const newContent = textAdAttributes[key].replace(/href="(\d+)"/g, 'href="javascript:void(0);popupOverlayId=$1"');
      return `<div class="${key}">${newContent}</div>`;
    })
    .join('');
};

const splitTextArray = (text) => text.reduce((result, str) => {
  const key = /\d$/.test(str) ? 'withNumbers' : 'withoutNumbers';
  result[key].push(str);
  return result;
}, { withNumbers: [], withoutNumbers: [] });

const numberedTextKeys = ref(splitTextArray(props.textArray).withNumbers);
const numberlessTextKeys = ref(splitTextArray(props.textArray).withoutNumbers);
const textAdContentTop = computed(() => createTextContent(numberlessTextKeys.value));
const textAdContentBottom = computed(() => createTextContent(numberedTextKeys.value));

const bgColor = props.applyGlobalStyles === true ? props.text?.attributes?.bgColor : '';
const textAlign = props.applyGlobalStyles ? props.text?.attributes?.alignment : '';
const fontColor = props.text?.attributes?.fontColor
  ? props.text?.attributes?.fontColor
  : 'inherit';
const eyebrowFontColor = props.text?.attributes?.eyebrowStyleType === 'boxedEyebrow' && props.text?.attributes?.fontColor === '#000000' && (props.text?.attributes?.eyebrowBgColor === undefined || null)
  ? 'white' : props.text?.attributes?.fontColor;
const logoMgnRight = props.text?.attributes?.alignment === 'right' ? 0 : 'auto';
const logoMgnLeft = props.text?.attributes?.alignment === 'left' ? 0 : 'auto';

const imagePath = props.text?.banner || props.text?.imageMap || undefined;

const imageUrl = `${imagePath?.urlTemplate}${imagePath?.filePath}${imagePath?.profile}?${imagePath?.preset}`;

const backgroundStyle = computed(() => {
  if (props.topLevelComp === true && imagePath) {
    return ({
      'background-color': imagePath?.attributes?.bgColor ? imagePath?.attributes?.bgColor : bgColor,
      'background-repeat': 'no-repeat',
      'background-position': 'center center',
      'background-size': 'cover',
      'background-image': `url(${imageUrl})`,
    });
  }
  return bgColor ? `background-color: ${bgColor}` : null;
});

const logoStyleAttributes = computed(() => (
  {
    'max-width': `${props.text?.logo?.width}px`,
    'max-height': `${props.text?.logo?.height}px`,
    'margin-left': `${logoMgnLeft}`,
    'margin-right': `${logoMgnRight}`,
    'margin-bottom': '1rem',
  }));

</script>

<style lang="scss" scoped>
.textad-wrapper {
  padding: rem-calc(40);

  @include breakpoint(large) {
    padding: rem-calc(48);
  }

}

.textad-wrapper> :last-child:not(.logo-block)> :last-child {
  margin-bottom: 0;
}

.textad-container :deep(*) {

  .text-block,
  .eyebrow {
    * {
      color: v-bind(eyebrowFontColor);
    }
  }

  .eyebrow,
  .caption,
  .body,
  .body2,
  .body3 {
    margin-bottom: rem-calc(16);
  }

  .ctas-cont {
    margin-bottom: 0;
  }

  .heading,
  .subHeading2,
  .subHeading3 {
    margin-bottom: rem-calc(4);
  }

  .subHeading {
    margin-bottom: rem-calc(8);
  }

  .chart-visible {
    > :last-child {
      margin-bottom: 0;
    }
  }

}
</style>

<style lang="scss" brand="mcom" scoped>
@import "./../../scss/globalAttributes.mcom.scss";
</style>

<style lang="scss" brand="bcom" scoped>
@import "./../../scss/globalAttributes.bcom.scss";
.boxedEyebrow {
  display: inline-block;
  padding: rem-calc(6px 20px 4px);
}

</style>
